import { useState } from 'react'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import PageContainer from '../components/layout/PageContainer'
import Button from '../components/common/Button'
import useAuthStore from '../store/useAuthStore'
import { formatDate } from '../utils/formatters'
import { stripeService } from '../services/api/stripeService'

const isSafeHttpsUrl = (value) => {
  if (!value) return false
  try {
    const url = new URL(value)
    return url.protocol === 'https:'
  } catch {
    return false
  }
}

const Billing = () => {
  const { user, subscription, hasActiveSubscription } = useAuthStore()
  const [isRedirectingToPortal, setIsRedirectingToPortal] = useState(false)

  const handleManageSubscription = async () => {
    setIsRedirectingToPortal(true)
    try {
      const response = await stripeService.createCustomerPortalSession({
        returnUrl: window.location.href,
      })
      const portalUrl = response?.url || response?.data?.url

      if (!isSafeHttpsUrl(portalUrl)) {
        throw new Error('Invalid billing portal link received')
      }

      window.location.assign(portalUrl)
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to open subscription management'
      toast.error(errorMessage)
      setIsRedirectingToPortal(false)
    }
  }

  return (
    <PageContainer
      title="Billing & Subscription"
      subtitle="View your current subscription details"
    >
      {subscription && (
        <section className="mb-6 bg-white border border-border-light rounded-xl p-5 md:p-6">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-text-dark">Current Subscription</h2>
            <div className="flex items-center gap-2">
              {subscription.subscriptionStatus && (
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  subscription.subscriptionStatus === 'trialing'
                    ? 'bg-blue-50 text-blue-700'
                    : subscription.subscriptionStatus === 'active'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                }`}>
                  {subscription.subscriptionStatus}
                </span>
              )}
              <Button
                variant="secondary"
                size="sm"
                icon="mdi:credit-card-settings-outline"
                onClick={handleManageSubscription}
                disabled={isRedirectingToPortal}
              >
                {isRedirectingToPortal ? 'Redirecting...' : 'Manage Subscription'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-xs text-text-secondary">Plan</p>
              <p className="mt-1 text-sm font-bold text-text-dark">{subscription.planName || 'N/A'}</p>
              {subscription.planId?.description && (
                <p className="mt-1 text-xs text-text-secondary">{subscription.planId.description}</p>
              )}
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-xs text-text-secondary">Billing Cycle</p>
              <p className="mt-1 text-sm font-bold text-text-dark capitalize">{subscription.interval || 'N/A'}</p>
              <p className="mt-1 text-xs text-text-secondary">${subscription.planId?.price || 0} / {subscription.interval || 'month'}</p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-xs text-text-secondary">Status</p>
              <p className="mt-1 text-sm font-bold capitalize text-text-dark">{subscription.subscriptionStatus || 'N/A'}</p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-xs text-text-secondary">Current Period</p>
              <p className="mt-1 text-xs font-semibold text-text-dark">
                {subscription.currentPeriodStart ? formatDate(subscription.currentPeriodStart) : 'N/A'}
              </p>
              <p className="mt-1 text-xs text-text-secondary">to</p>
              <p className="mt-1 text-xs font-semibold text-text-dark">
                {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'N/A'}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-xs text-text-secondary">Invoice</p>
              <p className="mt-1 text-xs font-bold capitalize text-text-dark">{subscription.invoiceStatus || 'N/A'}</p>
              {subscription.invoiceId && (
                <p className="mt-1 text-xs text-text-secondary truncate" title={subscription.invoiceId}>{subscription.invoiceId}</p>
              )}
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <p className="text-xs text-text-secondary">Cancel At Period End</p>
              <p className="mt-1 text-sm font-bold text-text-dark">{subscription.cancelAtPeriodEnd ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </section>
      )}

      {!hasActiveSubscription && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800">
          No active subscription found. Please contact support to get a plan.
        </div>
      )}
    </PageContainer>
  )
}

export default Billing
